import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Col, Row } from "react-bootstrap";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { useTheme } from "styled-components";
import classNames from "classnames";
import { Helmet } from "react-helmet-async";
import RecentTransactions from "shared/StrategyOverview/v2/Components/Tables/RecentTransactions";
import { PastPerformanceDisclaimer } from "shared/StrategyOverview/v2/Components/StrategyPageV2.styles";

import {
  StrategyDetailsContainer,
  CardContainer,
  NoDataMessage,
  DescriptionContainer,
  ReadMoreButton,
  // SubHeading,
} from "features/Strategy/v2/Detail/StrategyDetails.styles";
import {
  getAtVolumeFilteredDataExtended,
  getFilteredDrawdownData,
  getHpFilteredDataWithTimeframe,
  getHpLineColors,
  stripBenchmarkFromHpData,
  prepareHeatmapData,
  prepareTopHoldingsData,
} from "features/Strategy/utils";
import {
  StrategyPieChart,
  StrategyPredictionChart,
  AverageTradePerformanceChart,
  AssetTradingVolumeChart,
  HeatmapChart,
  HoldingsPieChart,
  GradientLineChart,
} from "shared";
import { useStrategyDetails } from "features/Strategy/hook";
import {
  IconAIGenerated,
  DownCaretIcon,
  UpCaretIcon,
} from "assets/icons/index";

// import { getYear } from "utils/date";
// import HeroTab from "shared/HeroTab/index";
import {
  KeyStats,
  Trades,
  Community,
  DetailsHeader,
  RiskPotential,
  BacktestLogs,
  BacktestFilter,
  SupportedBrokers,
  SectorAllocation,
  DescriptionPlaceholder,
  NoBacktest,
} from "./Components";
import { Tooltip } from "components/Tooltip";
// import { Accordion } from "components/Accordion";
import { Title } from "style/components/Title.styles";
import { isEmptyObj } from "utils";
import StrategyControlModal from "shared/StrategyControl";
import { scrollTopValueSelect } from "Layout/slice/selector";
import { contains, isFreeUser, isMobileDevice } from "utils/index";
// import { AnnualReturnsChart } from "shared/Chart/AnnualReturnsChart";
import RecommendCard from "features/Explore/v2/Components/RecommendCard/index";
import { HistoricalPerformanceChartV2 } from "shared/Chart/HistoricalPerformanceChart/v2";
import {
  LOCK_MODAL_CONFIG,
  THEME_TYPES,
  TIME_INDICATOR,
} from "utils/constants";
import { setLockModal } from "Layout/slice";
import ReturnValue from "features/Dashboard/v2/Components/AccountDetails/ReturnValue";
import {
  filterChartByTimeframe,
  getReturnSummary,
  getStrategyChartByKey,
  normalizeChartData,
} from "utils/charts";
import { CHART_VALUE_KEY } from "./v2/index";

// const TabOptions = [
//   { key: "performance", name: "Performance" },
//   { key: "summary", name: "Summary" },
//   { key: "assets", name: "Assets" },
//   { key: "drawdown", name: "Drawdown" },
//   { key: "returns", name: "Returns" },
// ];

const StrategyDetail = ({
  showBookmarkIcon = true,
  isBacktesting = false,
  runTest,
  setRunTest,
  waitForStrategy,
  showEditIcon = false,
  showHeroTab = true,
  isPublicStrategy = false,
  isOwnStrategy = false,
  strategyId = "",
  fromSignup = false,
  onClose = () => {},
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const id = strategyId || useParams().id;
  const isMobile = isMobileDevice();
  const [searchParams, setSearchParams] = useSearchParams();
  const scrollTopValue = useSelector(scrollTopValueSelect);

  const { isLoading, data: strategyDetailData } = useStrategyDetails(
    id,
    isPublicStrategy
  );
  const [backtestLoading, setBacktestLoading] = useState(false);
  const combinedLoading = isLoading || backtestLoading;

  const [logs, setLogs] = useState([]);
  const [strategyDetail, setStrategyDetail] = useState({});
  const [hPerformance, setHPerformance] = useState([]);
  const [chartLineColors, setChartLineColors] = useState([]);
  const [isBacktested, setIsBacktested] = useState(true);
  const [atVolume, setAtVolume] = useState([]);
  const [drawdown, setDrawdown] = useState([]);
  const [keyStats, setKeyStats] = useState([]);
  const [heatmapCounts, setHeatmapCounts] = useState({});
  const [activeHpFilter, setActiveHpFilter] = useState("Max");
  const [topHoldings, setTopHoldings] = useState([]);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [showStrategyControlModal, setShowStrategyControlModal] =
    useState(false);
  const [showKeyStatsModal, setshowKeyStatsModal] = useState(false);

  const isFreePlan = isFreeUser();
  const isHiddenContent = !isOwnStrategy
    ? isPublicStrategy || isFreePlan
    : false;

  const {
    frequency,
    trades,
    heatmap = [],
    prediction,
    industries,
    industries_benchmark,
  } = strategyDetail?.graphs ?? {};

  const descriptionRef = React.useRef(null);
  const darkTheme = theme.mode == THEME_TYPES.DARK;

  useEffect(() => {
    if (
      scrollTopValue == 0 &&
      contains(location.pathname, "explore/strategy") &&
      !fromSignup
    ) {
      searchParams.set("activeTab", "performance");
      setSearchParams(new URLSearchParams(searchParams.toString()));
    }
  }, [scrollTopValue, fromSignup]);

  useEffect(() => {
    if (showHeroTab && isBacktested) {
      const sectionIds = [
        "performance",
        "summary",
        "assets",
        "drawdown",
        "returns",
      ];

      const hoverDelay = 1000;
      let hoverTimer = null;

      const handleIntersect = (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !fromSignup) {
            searchParams.set("activeTab", entry.target.id);
            setSearchParams(new URLSearchParams(searchParams.toString())); // Re-create a new searchParams object
          }
        });
      };

      const observer = new IntersectionObserver(handleIntersect, {
        root: null,
        rootMargin: "131px",
        threshold: 0.7,
      });

      // Start the observer after a delay
      const startObserverAfterDelay = (element) => {
        hoverTimer = setTimeout(() => {
          observer.observe(element);
        }, hoverDelay);
      };

      // Clear the hover timer if user leaves before the time
      const clearHoverTimer = () => {
        if (hoverTimer) {
          clearTimeout(hoverTimer);
          hoverTimer = null;
        }
      };

      const setupHoverListeners = () => {
        sectionIds.forEach((id) => {
          const element = document.getElementById(id);
          if (element) {
            element.addEventListener("mouseenter", () =>
              startObserverAfterDelay(element)
            );
            element.addEventListener("mouseleave", clearHoverTimer);
          }
        });
      };

      setupHoverListeners();

      return () => {
        // Cleanup on unmount or when showHeroTab changes
        sectionIds.forEach((id) => {
          const element = document.getElementById(id);
          if (element) {
            element.removeEventListener("mouseenter", () =>
              startObserverAfterDelay(element)
            );
            element.removeEventListener("mouseleave", clearHoverTimer);
          }
        });
        observer.disconnect();
      };
    }
  }, [showHeroTab, isBacktested, searchParams, setSearchParams, fromSignup]);

  useEffect(() => {
    if (descriptionRef.current) {
      if (descriptionRef.current.scrollHeight == 80) {
        setIsOverflowing(false);
      } else {
        setIsOverflowing(descriptionRef.current.scrollHeight > 80);
      }
    }
  }, [strategyDetail.description]);

  // Process strategy data when it changes
  useEffect(() => {
    if (strategyDetailData) {
      setStrategyDetail(strategyDetailData);

      if (strategyDetailData.graphs && !isEmptyObj(strategyDetailData.graphs)) {
        const { heatmap, new_asset_trading_volume, raw_chart, top_holdings } =
          strategyDetailData.graphs || {};

        setIsBacktested(true);
        setKeyStats(strategyDetailData.key_stats);

        const filterData = getHpFilteredDataWithTimeframe(
          strategyDetailData.overview_chart
        );
        const filterDataNoBenchmark = stripBenchmarkFromHpData(filterData);
        setHPerformance(filterDataNoBenchmark);

        const defaultFilter = Object.keys(filterDataNoBenchmark)[0];
        const lineColors = getHpLineColors(
          filterDataNoBenchmark[defaultFilter]
        );
        setChartLineColors(lineColors);

        const resultAtv = getAtVolumeFilteredDataExtended(
          new_asset_trading_volume
        );
        setAtVolume(resultAtv);

        const heatMapData = prepareHeatmapData(heatmap);
        setHeatmapCounts(heatMapData);

        const resultDrawdown = getFilteredDrawdownData(raw_chart);
        setDrawdown(resultDrawdown);

        const filterTopHoldings = prepareTopHoldingsData(top_holdings);
        setTopHoldings(filterTopHoldings);

        if (strategyDetailData.logs) setLogs(strategyDetailData.logs);
      } else {
        setIsBacktested(false);
      }
    }
  }, [strategyDetailData]);

  useEffect(() => {
    if (activeHpFilter && hPerformance.length > 0) {
      const lineColors = getHpLineColors(hPerformance[activeHpFilter]);
      setChartLineColors(lineColors);
    }
  }, [activeHpFilter]);

  useEffect(() => {
    if (isHiddenContent) {
      const chartElement = document.querySelector(".historical-chart-card");
      if (chartElement) {
        const chartRect = chartElement.getBoundingClientRect();
        const stickyHeight = 200;

        const isChartVisible =
          chartRect.top < window.innerHeight - stickyHeight &&
          chartRect.bottom > stickyHeight;

        const scrolledToBottom =
          window.innerHeight + scrollTopValue >
          document.documentElement.offsetHeight;

        const shouldShowLock = !isChartVisible && scrolledToBottom;

        if (shouldShowLock) {
          dispatch(
            setLockModal({
              visible: true,
              ...LOCK_MODAL_CONFIG.strategyDetails,
            })
          );
        } else {
          dispatch(setLockModal({ visible: false }));
        }
      }
    }
  }, [scrollTopValue, isHiddenContent]);

  const handleFilterClick = (id) => {
    if (isHiddenContent && id !== "Max") {
      document.getElementById("blur-content")?.scrollIntoView({
        behavior: "smooth",
      });
      return;
    }

    setActiveHpFilter(id);
  };

  const getTrades = () => {
    if (!trades) return [];
    let _trades = trades;
    if (!isBacktesting) {
      _trades = _trades.slice(-25);
    }
    return [].concat(_trades).reverse();
  };

  const convertValue = (value) => (value ? `${value}%` : value);

  const drawdownColors = [
    {
      id: 1,
      key: "value",
      color: theme.chart.bar.primary,
    },
  ];

  // const handleTabClick = (filter) => {
  //   const container = document.querySelector(`#${filter.key}`);
  //   if (container) {
  //     const scrollOptions = {
  //       behavior: "smooth",
  //       alignToTop: true,
  //       block: "center",
  //     };
  //     container.scrollIntoView(scrollOptions);
  //   }
  // };

  const toggleStrategyControlModal = async () => {
    setShowStrategyControlModal(!showStrategyControlModal);
  };

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const toggleViewMoreModal = () => {
    setshowKeyStatsModal(!showKeyStatsModal);
  };

  const lastMonthSummary = useMemo(() => {
    const PERIOD = "1M";
    const threeMonthData = getStrategyChartByKey(
      strategyDetailData?.overview_chart || [],
      "3M" // from the backend as its not having key of 1M
    );
    const lastMonthData = filterChartByTimeframe(threeMonthData, PERIOD);
    const normalized = normalizeChartData(lastMonthData, CHART_VALUE_KEY);
    return getReturnSummary(normalized, TIME_INDICATOR[PERIOD]);
  }, [strategyDetailData?.overview_chart, getReturnSummary]);

  const allTimeSummary = useMemo(() => {
    const PERIOD = "MAX";
    const filtered = filterChartByTimeframe(
      getStrategyChartByKey(strategyDetailData?.overview_chart || [], PERIOD),
      PERIOD
    );
    const normalized = normalizeChartData(filtered, CHART_VALUE_KEY);
    return getReturnSummary(normalized, TIME_INDICATOR[PERIOD]);
  }, [strategyDetailData?.overview_chart, getReturnSummary]);

  return (
    <StrategyDetailsContainer
      className={classNames({
        "m-0 p-0": isBacktesting && !showHeroTab,
      })}
    >
      {isHiddenContent && (
        <Helmet>
          <meta name="robots" content="noindex, nofollow"></meta>
        </Helmet>
      )}

      <Col>
        <BacktestFilter
          runTest={runTest}
          setRunTest={setRunTest}
          strategyName={strategyDetail?.name}
          minimumInvestment={strategyDetail?.minimum_investment}
          waitForStrategy={waitForStrategy}
          start_date={strategyDetail?.start_date}
          end_date={strategyDetail?.end_date}
          setIsLoading={setBacktestLoading}
          canEditCode={strategyDetail.can_edit_code}
        />
        <Col lg={isBacktesting ? 12 : 3} xs={12} className="desktop-view">
          {isBacktesting && !isMobile ? (
            <></>
          ) : (
            <Row className="transparent-divider invest-main-container">
              <Col md={6} lg={12} className="mb-md-2 sticky-card">
                <CardContainer className="pb-0 m-0">
                  {!isBacktesting && (
                    <DetailsHeader
                      fromSignup={fromSignup}
                      isLoading={combinedLoading}
                      strategyDetail={strategyDetail}
                      showBookmarkIcon={showBookmarkIcon && !fromSignup}
                      showEditIcon={showEditIcon}
                      isPublicStrategy={isPublicStrategy}
                      showShareIcon={!fromSignup}
                      onClose={onClose}
                    />
                  )}
                </CardContainer>
              </Col>

              <Col md={6} lg={12} className="mb-2 mb-md-2">
                <CardContainer className="m-0">
                  <SupportedBrokers
                    supportedBrokers={strategyDetail.supported_brokers}
                    isLoading={combinedLoading}
                  />
                </CardContainer>
              </Col>
              <Col md={6} lg={12} className="mb-2 mb-md-2">
                <CardContainer className="m-0">
                  {" "}
                  <RiskPotential data={keyStats} />
                </CardContainer>
              </Col>
              {!fromSignup && (
                <Col md={6} lg={12} className="recommend-card-col mb-2 mb-md-2">
                  <RecommendCard />
                </Col>
              )}
              <Col md={6} lg={12} className="d-lg-none">
                <Community strategyName={strategyDetail?.name} />
              </Col>
            </Row>
          )}
        </Col>

        <div className="wt-investment-strategy-details position-relative">
          {/* no backtest */}
          {!isBacktested && !isLoading && <NoBacktest />}
          {/* 
          <div className="mobile-view pt-0">
            {isBacktesting && isMobile && (
              <Accordion
                heading="Predefined Scenarios"
                className="predefined-scenarios"
              >
                <BacktestFilter
                  runTest={runTest}
                  setRunTest={setRunTest}
                  strategyName={strategyDetail?.name}
                  waitForStrategy={waitForStrategy}
                  start_date={strategyDetail?.start_date}
                  end_date={strategyDetail?.end_date}
                  setIsLoading={setBacktestLoading}
                  canEditCode={strategyDetail.can_edit_code}
                />
              </Accordion>
            )}
          </div> */}

          {isBacktested && (
            <>
              <Col className="mb-2 mb-lg-0">
                {/* {showHeroTab && (
                  <HeroTab
                    isLoading={isLoading}
                    title="Raise the Volume!"
                    tabAppendParams={!fromSignup}
                    subTitle="Ayush Rautwar13"
                    data={{ authorId: strategyDetail?.author_id }}
                    tabOptions={TabOptions}
                    handleTabClick={handleTabClick}
                  />
                )} */}

                <div className="historical-performance position-relative">
                  <div id="performance" />

                  <CardContainer className="historical-chart-card">
                    <Title>Historical performance</Title>
                    <HistoricalPerformanceChartV2
                      data={hPerformance[activeHpFilter]}
                      lines={chartLineColors}
                      activePeriod={activeHpFilter}
                      handleFilterClick={handleFilterClick}
                      timeframe={Object.keys(hPerformance)}
                      isLoading={combinedLoading}
                      showLegend={false}
                      showTopTooltipProfit={false}
                      // referenceLineProps={{ y: 10000 }}
                    />
                  </CardContainer>

                  <PastPerformanceDisclaimer>
                    Past performance does not guarantee future results.
                  </PastPerformanceDisclaimer>

                  <Row>
                    <Col md={6}>
                      <ReturnValue
                        title="Last month's return"
                        {...lastMonthSummary}
                      />
                    </Col>
                    <Col md={6}>
                      <ReturnValue title="Total return" {...allTimeSummary} />
                    </Col>
                  </Row>

                  <Row className="pt-0 mobile-view">
                    <Col className="my-3 ">
                      <SupportedBrokers
                        supportedBrokers={strategyDetail.supported_brokers}
                        isLoading={combinedLoading}
                      />
                    </Col>
                    <Col className="mb-3">
                      <RiskPotential data={keyStats} />
                    </Col>
                  </Row>

                  <div id="summary">
                    {combinedLoading ? (
                      <DescriptionPlaceholder />
                    ) : (
                      <CardContainer className="description-wrapper">
                        <div className="d-flex">
                          <Title>Description</Title>

                          {strategyDetail?.ai_generated_strategy && (
                            <Tooltip
                              value="Written using Surmount AI"
                              tooltipClassName="v2-tooltip"
                            >
                              <IconAIGenerated />
                            </Tooltip>
                          )}
                        </div>

                        <div className="mb-0 description-content">
                          <DescriptionContainer
                            isExpanded={isExpanded}
                            className={classNames({
                              "description-expanded": isExpanded,
                              "description-collapsed": !isExpanded,
                            })}
                          >
                            <p ref={descriptionRef}>
                              {strategyDetail.description
                                ? strategyDetail.description
                                : "Not available"}
                            </p>
                          </DescriptionContainer>

                          {isOverflowing && !isExpanded && (
                            <div
                              className={classNames("btn-read-more", {
                                "btn-dark-read-more": darkTheme,
                              })}
                            >
                              <span className="dot-fillers">...</span>
                              <ReadMoreButton
                                onClick={toggleExpanded}
                                className={classNames({
                                  "dark-btn": darkTheme,
                                })}
                              >
                                Read More <DownCaretIcon />
                              </ReadMoreButton>
                            </div>
                          )}
                          {isExpanded && (
                            <div
                              className={classNames("btn-read-more", {
                                "btn-dark-read-more": darkTheme,
                              })}
                            >
                              <ReadMoreButton
                                onClick={toggleExpanded}
                                className={classNames({
                                  "dark-btn": darkTheme,
                                })}
                              >
                                Read Less <UpCaretIcon />
                              </ReadMoreButton>
                            </div>
                          )}
                        </div>
                      </CardContainer>
                    )}
                  </div>
                </div>

                <div
                  id="blur-content"
                  className={classNames("position-relative", {
                    "blur-overlay": isHiddenContent,
                  })}
                >
                  {/* <CardContainer> */}
                  <div className="key-metrics-wrapper">
                    <div className="title-wraper">
                      <Title>Key metrics</Title>

                      <button
                        className="view-more"
                        onClick={toggleViewMoreModal}
                      >
                        Show more
                      </button>
                    </div>
                    <KeyStats
                      stats={keyStats}
                      isLoading={combinedLoading}
                      showKeyStatsModal={showKeyStatsModal}
                      setshowKeyStatsModal={setshowKeyStatsModal}
                    />
                  </div>
                  {/* </CardContainer> */}

                  <div className="w-100">
                    <CardContainer className="asset-allocation">
                      <SectorAllocation
                        isLoading={combinedLoading}
                        isPublicStrategy={isHiddenContent}
                        data={{
                          industries: industries,
                          industries_benchmark: industries_benchmark,
                        }}
                      />
                      <div className="desktop-view d-none">
                        <StrategyPieChart />
                      </div>
                      <div className="mobile-view current-alloc-mobile d-none">
                        <StrategyPieChart
                          legendProps={{
                            align: "bottom",
                          }}
                        />
                      </div>
                    </CardContainer>
                  </div>

                  <div className="w-100">
                    <CardContainer className="historical-allocation">
                      <Title className="mb-0">Top holdings</Title>
                      <div className="divider my-3"></div>
                      <div className="desktop-top-holdings">
                        <HoldingsPieChart
                          isLoading={combinedLoading}
                          data={topHoldings}
                        />
                      </div>
                      <div className="mobile-top-holdings">
                        <HoldingsPieChart
                          innerRadius={60}
                          data={topHoldings}
                          legendProps={{
                            align: "bottom",
                            verticalAlign: "bottom",
                            layout: "horizontal",
                          }}
                          isLoading={combinedLoading}
                        />
                      </div>
                      {!combinedLoading && topHoldings.length === 0 && (
                        <NoDataMessage>No data to display</NoDataMessage>
                      )}
                    </CardContainer>
                  </div>

                  <div className="w-100">
                    <CardContainer className="trade-performance">
                      <Title className="mb-0">Average trade performance</Title>
                      <div className="divider my-3"></div>

                      <AverageTradePerformanceChart
                        data={frequency}
                        barDataKey="performance"
                        margin={{
                          left: 0,
                          top: 10,
                        }}
                        height="300px"
                        isLoading={combinedLoading}
                      />
                      {!combinedLoading &&
                        (!frequency || frequency?.length === 0) && (
                          <NoDataMessage>No data to display</NoDataMessage>
                        )}
                    </CardContainer>
                  </div>

                  <div className="w-100">
                    <CardContainer className="trade-performance" id="assets">
                      <AssetTradingVolumeChart
                        data={atVolume}
                        isLoading={combinedLoading}
                        title="Asset trading volume"
                      />
                    </CardContainer>
                  </div>

                  <Row className="d-none">
                    <Col>
                      <CardContainer className="trade-performance">
                        <Title className="mb-0">Strategy Prediction</Title>
                        <div className="divider my-3"></div>
                        <StrategyPredictionChart chart={prediction} />
                      </CardContainer>
                    </Col>
                  </Row>

                  <div className="w-100">
                    <CardContainer
                      className="historical-allocation"
                      id="drawdown"
                    >
                      <Title className="mb-0">Drawdown</Title>
                      <div className="divider my-3"></div>
                      {drawdown.length > 0 ? (
                        <GradientLineChart
                          data={drawdown}
                          isLoading={combinedLoading}
                          title=""
                          gridVertical
                          fillOpacity={1}
                          assetsColors={drawdownColors}
                          height={"300px"}
                          showXAxis={false}
                          showGridLines={false}
                          areaProps={{
                            isAnimationActive: false,
                            strokeWidth: 0,
                          }}
                          yAxisProps={{
                            fontSize: 12,
                            stroke: theme.chart.axis.primary,
                            domain: ["auto", 0],
                            tickFormatter: convertValue,
                          }}
                        />
                      ) : (
                        <NoDataMessage>No data to display</NoDataMessage>
                      )}
                    </CardContainer>
                  </div>

                  <div className="w-100">
                    <RecentTransactions strategyId={id} />
                  </div>
                  <div className="d-none">
                    <CardContainer className="asset-allocation">
                      {/* <SubHeading className="my-4">
                        Activity in {getYear()}
                      </SubHeading> */}
                      {!isEmptyObj(heatmapCounts) && (
                        <HeatmapChart
                          startDate={heatmapCounts?.start}
                          endDate={heatmapCounts?.end}
                          values={heatmapCounts?.counts}
                        />
                      )}
                      {!heatmap?.length && (
                        <NoDataMessage>No data to display</NoDataMessage>
                      )}
                    </CardContainer>
                  </div>

                  {/* <div className="w-100">
                    <CardContainer className="calendar-returns" id="returns">
                      <AnnualReturnsChart
                        title="Annual return"
                        data={{
                          calendar_years: backtestGraphs.calendar_years,
                          calendar_years_benchmark:
                            backtestGraphs.calendar_years_benchmark,
                        }}
                        isLoading={combinedLoading}
                        isPublicStrategy={isHiddenContent}
                      />
                    </CardContainer>
                  </div> */}

                  <div className="w-100">
                    <CardContainer className="trades-wrapper">
                      <Trades
                        data={getTrades()}
                        isLoading={combinedLoading}
                        isPublicStrategy={isHiddenContent}
                      />
                    </CardContainer>
                  </div>

                  <div className="w-100">
                    <p className="no-data">
                      The information presented is for educational purposes only
                      and not an offer or solicitation for any specific
                      investments. Investments involve risk and are not
                      guaranteed. Consult with a financial adviser before making
                      any investment decisions. Past performance does not
                      guarantee future results.
                    </p>
                  </div>
                </div>
              </Col>

              {logs.length > 0 && (
                <Row>
                  <Col>
                    <CardContainer className="logs-wrapper">
                      <BacktestLogs data={logs} isLoading={combinedLoading} />
                    </CardContainer>
                  </Col>
                </Row>
              )}
            </>
          )}
        </div>
      </Col>
      {showStrategyControlModal && (
        <StrategyControlModal
          show={showStrategyControlModal}
          onHide={toggleStrategyControlModal}
          selectedStrategyData={strategyDetail}
        />
      )}

      <Row className="mobile-view mobile-transparent-divider invest-main-container fixed-bottom-row">
        <Col md={6} lg={12} className="mb-md-2 sticky-card">
          <CardContainer className="pb-0 m-0">
            {!isBacktesting && (
              <DetailsHeader
                isLoading={combinedLoading}
                strategyDetail={strategyDetail}
                showBookmarkIcon={showBookmarkIcon}
                showEditIcon={showEditIcon}
                isPublicStrategy={isHiddenContent}
                showShareIcon={true}
              />
            )}
          </CardContainer>
        </Col>
      </Row>
    </StrategyDetailsContainer>
  );
};

StrategyDetail.propTypes = {
  showBackTesting: PropTypes.bool,
  showLicense: PropTypes.bool,
  showEditDescription: PropTypes.bool,
  isBacktesting: PropTypes.bool,
  showBookmarkIcon: PropTypes.bool,
  runTest: PropTypes.number,
  waitForStrategy: PropTypes.func,
  setRunTest: PropTypes.func,
  showEditIcon: PropTypes.bool,
  showBackButton: PropTypes.bool,
  isPublicStrategy: PropTypes.bool,
  showHeroTab: PropTypes.bool,
  isOwnStrategy: PropTypes.bool,
  strategyId: PropTypes.string,
  fromSignup: PropTypes.bool,
  onClose: PropTypes.func,
};

export default StrategyDetail;
